import React, { useState } from "react";
import { useNavigation, useTermsAgreements } from "../../../hooks";
import { signupService } from "../../../services/signupService.ts";
import { STORAGE_KEYS } from "../../../types/storage.ts";
import { TermsListItem } from "../../../types/terms";
import { TERMS_URLS } from "../../../utils";
import { ErrorMessage, LoadingSpinner } from "../../common";
import { BottomActionButton } from "../../ui";
import Icon from "../../ui/Icon";
import Modal from "../../ui/Modal";

const SHOW_TESTER_ID = import.meta.env.VITE_SHOW_TESTER_ID === "true";

const TermsAgreementScreen: React.FC = () => {
  const { goBack } = useNavigation();

  const {
    termsItems,
    datasetTerms,
    frontendAgreements,
    serverAgreements,
    isLoading,
    isAllRequiredAgreed,
    handleFrontendAgreementChange,
    handleServerAgreementChange,
    handleAgreeAll,
  } = useTermsAgreements();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<TermsListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testerId, setTesterId] = useState("");

  // 약관 상세보기 모달
  const handleTermClick = (term: TermsListItem) => {
    setSelectedTerm(term);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTerm(null);
  };

  // 외부 링크로 약관 페이지 열기
  const openTermsInNewWindow = (url: string) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // 본인인증 페이지로 form POST 제출
  const submitCertificationForm = (apiUrl: string, encryptData: string) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = apiUrl;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "encryptData";
    input.value = encryptData;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  };

  // 다음 버튼 클릭 (제출)
  const handleNext = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      sessionStorage.setItem(STORAGE_KEYS.USER_TERMS_AGREEMENT_LIST, JSON.stringify(frontendAgreements));
      const response = await signupService.requestSignupVerification({
        userTermsAgreementList: serverAgreements.map((agreement) => ({
          termsNo: agreement.termsNo,
          agreementStatus: agreement.agreed ? "AGREEMENT" : "NONE",
        })),
        ...(SHOW_TESTER_ID && testerId.trim() && { testerId: testerId.trim() }),
      });

      // form POST 방식으로 본인인증 서비스 호출
      submitCertificationForm(response.apiUrl, response.encryptData);
    } catch (error) {
      console.error("약관 처리 실패:", error);
      setError("약관 처리에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 버튼 클릭 핸들러 (모두 동의 또는 다음)
  const handleButtonClick = () => {
    if (isAllRequiredAgreed) {
      handleNext();
    } else {
      handleAgreeAll();
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center border-b border-gray-200 flex-shrink-0">
        <button
          onClick={goBack}
          className="flex items-center justify-center w-8 h-8 mr-3 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevronLeft" className="text-gray-600" />
        </button>
        <h1 className="text-lg font-medium text-gray-800">약관동의</h1>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto px-4 py-6 pb-4 md:pb-6">
        {error && (
          <div className="mb-4">
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          </div>
        )}

        {/* AI홈닥터 약관 그룹 */}
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900 mb-3 px-1">
            AI홈닥터 약관
          </h2>
          <div className="space-y-2">
            {/* 1. 만 14세 이상 확인 */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frontendAgreements.ageOverFourteenAgreement}
                    onChange={(e) =>
                      handleFrontendAgreementChange(
                        "ageOverFourteenAgreement",
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-800">
                    <span className="text-red-600 font-semibold mr-2 text-xs">
                      [필수]
                    </span>
                    만 14세 입니다.
                  </span>
                </label>
              </div>
            </div>

            {/* 2. AI홈닥터 서비스이용약관 */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frontendAgreements.aiHomedoctorServiceAgreement}
                    onChange={(e) =>
                      handleFrontendAgreementChange(
                        "aiHomedoctorServiceAgreement",
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-800">
                    <span className="text-red-600 font-semibold mr-2 text-xs">
                      [필수]
                    </span>
                    AI홈닥터 서비스이용약관 동의
                  </span>
                </label>
                <button
                  className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                  onClick={() => openTermsInNewWindow(TERMS_URLS.SERVICE_TERMS)}
                >
                  <Icon name="chevronRight" size="sm" />
                </button>
              </div>
            </div>

            {/* 3. 마케팅 목적 개인정보 수집 및 이용 동의 */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frontendAgreements.marketingInfoCollectionAgreement}
                    onChange={(e) =>
                      handleFrontendAgreementChange(
                        "marketingInfoCollectionAgreement",
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-800">
                    <span className="text-gray-500 font-semibold mr-2 text-xs">
                      [선택]
                    </span>
                    마케팅 목적 개인정보 수집 및 이용 동의
                  </span>
                </label>
                <button
                  className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                  onClick={() =>
                    openTermsInNewWindow(TERMS_URLS.MARKETING_CONSENT)
                  }
                >
                  <Icon name="chevronRight" size="sm" />
                </button>
              </div>
            </div>

            {/* 4. 마케팅정보 수신동의 */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frontendAgreements.marketingInfoReceiptAgreement}
                    onChange={(e) =>
                      handleFrontendAgreementChange(
                        "marketingInfoReceiptAgreement",
                        e.target.checked
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-800">
                    <span className="text-gray-500 font-semibold mr-2 text-xs">
                      [선택]
                    </span>
                    마케팅정보 수신동의
                  </span>
                </label>
                <button
                  className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                  onClick={() =>
                    openTermsInNewWindow(TERMS_URLS.MARKETING_INFO_RECEPTION)
                  }
                >
                  <Icon name="chevronRight" size="sm" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 의료마이데이터 약관 그룹 */}
        {termsItems.length > 0 && (
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 px-1">
              의료마이데이터 약관
            </h2>
            <div className="space-y-2">
              {termsItems.map((term) => {
                const agreement = serverAgreements.find(
                  (a) => a.termsNo === term.termsNo
                );
                const isAgreed = agreement?.agreed || false;

                return (
                  <div
                    key={term.termsNo}
                    className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <label className="flex items-center flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAgreed}
                          onChange={(e) =>
                            handleServerAgreementChange(
                              term.termsNo,
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-800">
                          <span className="text-red-600 font-semibold mr-2 text-xs">
                            [필수]
                          </span>
                          {term.termsName}
                        </span>
                      </label>
                      <button
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                        onClick={() => handleTermClick(term)}
                      >
                        <Icon name="chevronRight" size="sm" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 공공기관 약관 그룹 */}
        {datasetTerms.length > 0 && (
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 px-1">
              공공기관 약관
            </h2>
            <div className="space-y-2">
              {datasetTerms.map((term) => {
                const agreement = serverAgreements.find(
                  (a) => a.termsNo === term.termsNo
                );
                const isAgreed = agreement?.agreed || false;

                return (
                  <div
                    key={term.termsNo}
                    className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <label className="flex items-center flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAgreed}
                          onChange={(e) =>
                            handleServerAgreementChange(
                              term.termsNo,
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-800">
                          <span className="text-red-600 font-semibold mr-2 text-xs">
                            [필수]
                          </span>
                          {term.termsName}
                        </span>
                      </label>
                      <button
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                        onClick={() => handleTermClick(term)}
                      >
                        <Icon name="chevronRight" size="sm" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* AI홈닥터 개인정보 수집 및 이용 동의 - 부가 정보 */}
        <div className="mt-4 px-1">
          <p className="text-xs text-gray-500 mb-2">
            AI홈닥터 가입을 위한 개인정보 수집 및 이용에 동의합니다.
          </p>
          <button
            className="text-xs text-gray-400 hover:text-blue-500 transition-colors flex items-center"
            onClick={() =>
              openTermsInNewWindow(
                TERMS_URLS.AI_HOMEDOCTOR_PERSONAL_INFO_CONSENT
              )
            }
          >
            <span className="mr-1">•</span>
            AI 홈닥터 개인정보 수집 및 이용동의
            <Icon name="chevronRight" size="sm" className="ml-1" />
          </button>
        </div>

        {SHOW_TESTER_ID && (
          <div className="mt-4 pb-20 md:pb-4 px-1">
            <label className="block text-xs text-gray-500 mb-2">
              테스터 ID (선택)
            </label>
            <input
              type="text"
              value={testerId}
              onChange={(e) => setTesterId(e.target.value)}
              placeholder="테스터 ID를 입력하세요"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}
      </main>

      {/* 하단 버튼 */}
      <BottomActionButton
        onClick={handleButtonClick}
        isLoading={isSubmitting}
        loadingText="처리 중..."
      >
        {isAllRequiredAgreed ? "다음" : "모두 동의하기"}
      </BottomActionButton>

      {/* 약관 내용 모달 */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={selectedTerm?.termsName || ""}
      >
        <div
          className="text-sm text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{
            __html: selectedTerm?.termsHtmlContents || "",
          }}
        />
      </Modal>
    </div>
  );
};

export default TermsAgreementScreen;
